import { useState, type ReactNode } from 'react'
import { Sparkline } from '../../charts'
import {
  CashPositionChart,
  CoverBarsChart,
  MarginTrendChart,
  RankedBarsChart,
  TargetBulletsChart,
  WeeksBarsChart,
} from '../../charts/cardVisuals'
import { basisDelta, selectScorecardView, trendValues } from '../ceoCockpitModel'
import {
  BASIS_LABEL,
  computeDeltaGrammar,
  formatCurrencyCompact,
  formatCurrencyExact,
  formatDeltaGrammar,
  formatShare,
  formatSignedPct,
} from '../ceoFormat'
import type {
  CeoBasisKind,
  CeoCadenceItem,
  CeoCockpitResponse,
  CeoDecision,
  CeoDimensionKind,
  CeoDimensionRow,
  CeoMetricId,
  CeoPolarity,
  CeoPeriodKind,
  CeoScorecardMetric,
} from '../ceoCockpitTypes'
import type { CeoExploreSection } from '../ceoCockpitUrlState'

export type CeoExploreFrameProps = {
  payload: CeoCockpitResponse
  period: CeoPeriodKind
  basis: CeoBasisKind
  section: CeoExploreSection
  onSectionChange: (section: CeoExploreSection) => void
  onLensChange?: (lens: CeoDimensionKind) => void
  onExplainDecision?: (decision: CeoDecision) => void
  panels: Partial<Record<CeoExploreSection, ReactNode>>
}

type ExploreDestination = {
  id: CeoExploreSection
  label: string
  metricId?: CeoMetricId
}

const METRIC_DESTINATIONS: Partial<Record<CeoMetricId, CeoExploreSection>> = {
  sales: 'sales',
  revenue: 'sales',
  gross_profit: 'profit',
  net_income: 'profit',
  cash: 'cash',
  accounts_receivable: 'customers',
  mrr: 'subscriptions',
}

const PANEL_LABELS: Record<CeoExploreSection, string> = {
  sales: 'Sales',
  profit: 'Profit',
  cash: 'Cash',
  customers: 'Customers',
  scorecard: 'Scorecard',
  where: 'Where it comes from',
  people: 'People',
  inventory: 'Inventory',
  subscriptions: 'Subscriptions',
  morning: 'This morning',
}

function sectionTitle(section: CeoExploreSection, payload: CeoCockpitResponse): string {
  const month = new Date(`${payload.as_of_date.slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long' })
  if (section === 'sales') return `How ${month} is going`
  if (section === 'profit') return 'Profit by month'
  if (section === 'cash') return 'Where cash stands'
  if (section === 'customers') return 'Your biggest customers'
  if (section === 'scorecard') return 'Every measure against its goal'
  if (section === 'where') return 'Sales by location'
  if (section === 'people') return 'Labor as a share of sales'
  if (section === 'inventory') return 'Running low'
  if (section === 'subscriptions') return 'Recurring revenue health'
  return 'Where things stand'
}

function metricForSection(
  payload: CeoCockpitResponse,
  section: CeoExploreSection,
): CeoScorecardMetric | null {
  const preferred: Partial<Record<CeoExploreSection, CeoMetricId[]>> = {
    sales: ['sales', 'revenue'],
    profit: ['net_income', 'gross_profit'],
    cash: ['cash'],
    // Customers is an operating mix question in Explore. A/R is one of its
    // alternate views, not the headline number for the whole section.
    subscriptions: ['mrr'],
  }
  const ids = preferred[section] || []
  const scorecard = Array.isArray(payload.scorecard) ? payload.scorecard : []
  return ids.map((id) => scorecard.find((metric) => metric.id === id)).find(Boolean) || null
}

function metricReading(
  metric: CeoScorecardMetric | null,
  period: CeoPeriodKind,
  basis: CeoBasisKind,
  payload: CeoCockpitResponse,
): string {
  if (!metric) return `Written from the books through ${payload.as_of_date}.`
  const block = metric[period]
  const window = payload.calendar?.[period]
  const day = window?.days_elapsed
  const days = window?.days_in_period
  const pace = block?.pace?.projection
  const position = basisDelta(block, basis)
  const tone = position.deltaAbs == null ? '' : position.deltaAbs < 0 ? 'behind' : position.deltaAbs > 0 ? 'ahead' : 'on'
  const parts = [
    day && days ? `Day ${day} of ${days}` : null,
    tone ? `${tone} ${BASIS_LABEL[basis]}` : null,
    pace != null && Number.isFinite(pace) ? `on pace for ${formatCurrencyExact(pace)}` : null,
  ].filter(Boolean)
  return `${parts.join(' · ')}${parts.length ? '.' : ''}`
}

function decisionsForSection(payload: CeoCockpitResponse, section: CeoExploreSection): CeoDecision[] {
  const decisions = Array.isArray(payload.decisions) ? payload.decisions : []
  const refs: Partial<Record<CeoExploreSection, string[]>> = {
    sales: ['sales', 'revenue'],
    profit: ['gross_profit', 'net_income', 'profit', 'margin'],
    cash: ['cash', 'runway'],
    customers: ['accounts_receivable', 'customer', 'collections', 'dso'],
    where: ['location', 'channel', 'class', 'sales_rep', 'item'],
    people: ['labor', 'employee', 'headcount', 'utilization'],
    inventory: ['inventory', 'stock', 'sku', 'item'],
    subscriptions: ['mrr', 'arr', 'retention', 'churn', 'subscription'],
  }
  const needles = refs[section] || []
  const matched = decisions.filter((decision) => {
    const haystack = [decision.rule, decision.headline, ...decision.metric_refs, ...decision.tags]
      .join(' ')
      .toLowerCase()
    return needles.some((needle) => haystack.includes(needle))
  })
  return (matched.length ? matched : decisions).slice(0, 5)
}

type ExploreRead = { id: string; headline: string; body: string; severity: string; decision?: CeoDecision }

function governedReads(payload: CeoCockpitResponse, section: CeoExploreSection, period: CeoPeriodKind, basis: CeoBasisKind, view: string): ExploreRead[] {
  if (section === 'where') {
    const dim = dimensionForView(payload, view)
    const rows = dim?.rows || []
    const behind = rows.map((row) => ({ row, actual: row[period]?.actual || 0, ref: rowReference(row, period, basis) })).filter(({ ref }) => finite(ref) && ref > 0).sort((a, b) => (a.actual - (a.ref || 0)) - (b.actual - (b.ref || 0)))
    const marginRows = rows.filter((row) => finite(row.gross_margin_pct_ytd)).sort((a, b) => (b.gross_margin_pct_ytd || 0) - (a.gross_margin_pct_ytd || 0))
    const coverage = dim?.availability.coverage_pct
    const reads: ExploreRead[] = behind.slice(0, 3).map(({ row, actual, ref }, index) => ({ id: `where-gap-${row.key}`, severity: index ? 'medium' : 'high', headline: `${row.label} is ${actual < (ref || 0) ? 'behind' : 'ahead of'} ${BASIS_LABEL[basis]}.`, body: finite(ref) && ref !== 0 ? `${formatSignedPct((actual - ref) / Math.abs(ref))} against the expected result by now.` : '' }))
    if (finite(coverage) && coverage < 0.999) reads.push({ id: 'where-coverage', severity: 'low', headline: `${dim?.label || 'This lens'} covers ${formatShare(coverage)} of sales.`, body: `The remaining ${formatShare(1 - coverage)} is not coded to this lens.` })
    if (rows[0]) reads.push({ id: 'where-mix', severity: 'low', headline: `${rows[0].label} is the largest contributor.`, body: `${formatShare(rows[0].share_ytd)} of year-to-date sales.` })
    if (marginRows[0] && marginRows[marginRows.length - 1]) reads.push({ id: 'where-margin', severity: 'low', headline: `Best margin ${marginRows[0].label} (${formatShare(marginRows[0].gross_margin_pct_ytd)}).`, body: `Thinnest ${marginRows[marginRows.length - 1].label} (${formatShare(marginRows[marginRows.length - 1].gross_margin_pct_ytd)}).` })
    return reads.slice(0, 6)
  }
  if (section === 'inventory' && payload.inventory) {
    const inv = payload.inventory
    const topMargin = (inv.top_by_margin || [])[0]
    const topCategory = (inv.by_category || [])[0]
    return [
      { id: 'inv-low', severity: inv.stockout_count ? 'high' : 'low', headline: `${inv.stockout_count} ${inv.stockout_count === 1 ? 'product is' : 'products are'} at stockout risk.`, body: 'Under the replenishment threshold at current velocity.' },
      { id: 'inv-value', severity: 'low', headline: `${formatCurrencyExact(inv.on_hand_value)} on the shelf.`, body: `${Math.round(inv.on_hand_units).toLocaleString()} units${finite(inv.days_of_supply) ? ` · ${Math.round(inv.days_of_supply)} days of supply overall` : ''}.` },
      { id: 'inv-overstock', severity: inv.overstock.length ? 'medium' : 'low', headline: `${inv.overstock.length} products look overstocked.`, body: `${formatCurrencyExact(inv.overstock.reduce((sum, sku) => sum + sku.on_hand_value, 0))} tied up beyond the overstock window.` },
      ...(topMargin ? [{ id: 'inv-margin', severity: 'low', headline: `Best margin: ${topMargin.item_name}.`, body: topMargin.revenue_30d ? `${formatShare(topMargin.margin_30d / topMargin.revenue_30d)} gross margin in the last 30 days.` : 'Margin recorded from the latest sales window.' }] : []),
      ...(topCategory ? [{ id: 'inv-category', severity: 'low', headline: `${topCategory.label} is the biggest category on hand.`, body: `${formatCurrencyExact(topCategory[period]?.actual ?? topCategory.ytd?.actual ?? 0)} of inventory value.` }] : []),
    ].slice(0, 6)
  }
  return []
}

function ribbonTone(metric: CeoScorecardMetric, period: CeoPeriodKind, basis: CeoBasisKind): string {
  const delta = metric.kind === 'point_in_time'
    ? metric[period]?.prior_close?.delta_abs ?? metric[period]?.prior_period?.delta_abs ?? null
    : basisDelta(metric[period], basis).deltaAbs
  return computeDeltaGrammar({ deltaAbs: delta, polarity: metric.polarity }).tone
}

function compactDelta(metric: CeoScorecardMetric, period: CeoPeriodKind, basis: CeoBasisKind): string {
  const block = metric[period]
  if (metric.kind === 'point_in_time') {
    const delta = block?.prior_close?.delta_abs ?? block?.prior_period?.delta_abs
    return delta == null || !Number.isFinite(delta)
      ? 'as of yesterday'
      : `${delta >= 0 ? '▲' : '▼'} ${formatCurrencyExact(Math.abs(delta))} vs last close`
  }
  const delta = basisDelta(block, basis)
  return formatDeltaGrammar({ ...delta, basis, polarity: metric.polarity })
}

function inlineDelta(
  deltaAbs: number | null | undefined,
  deltaPct: number | null | undefined,
  polarity: CeoPolarity,
): { text: string; tone: string } | null {
  if (deltaAbs == null || !Number.isFinite(deltaAbs)) return null
  const grammar = computeDeltaGrammar({ deltaAbs, deltaPct, polarity })
  const pct = deltaPct == null || !Number.isFinite(deltaPct) ? '' : ` · ${formatSignedPct(deltaPct)}`
  return {
    text: `${grammar.arrow} ${formatCurrencyExact(Math.abs(deltaAbs))}${pct}`,
    tone: grammar.tone,
  }
}

function collectionsRibbonItem(payload: CeoCockpitResponse): CeoCadenceItem | null {
  const cadences = payload.cadences
  const items = cadences
    ? [...(cadences.today || []), ...(cadences.week || []), ...(cadences.month || [])]
    : []
  return items.find((item) => item.family === 'collections' && item.exposure_value != null) || null
}

function collectionsDetail(item: CeoCadenceItem): string {
  const withoutRepeatedValue = item.exposure_label.replace(/^[-+$\d.,KMB]+\s*/i, '').trim()
  return withoutRepeatedValue || 'receivables past due'
}

type OperatingRow = {
  label: string
  group: 'Outcome' | 'Controllable input'
  current: string
  currentValue?: number | null
  targetValue?: number | null
  goal: string
  owner: string
  status: 'On' | 'Off' | 'No goal'
  tone: 'good' | 'bad' | 'quiet'
  trend?: number[]
  trendLabel?: string
}

function finite(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value)
}

function periodMetric(payload: CeoCockpitResponse, id: CeoMetricId) {
  return (Array.isArray(payload.scorecard) ? payload.scorecard : []).find((metric) => metric.id === id)
}

function operatingRows(payload: CeoCockpitResponse, period: CeoPeriodKind, basis: CeoBasisKind): OperatingRow[] {
  const sales = periodMetric(payload, 'sales') || periodMetric(payload, 'revenue')
  const gp = periodMetric(payload, 'gross_profit')
  const cash = periodMetric(payload, 'cash')
  const salesBlock = sales?.[period]
  const gpBlock = gp?.[period]
  const salesDelta = sales && salesBlock ? basisDelta(salesBlock, basis) : null
  const grossMargin = finite(gpBlock?.actual) && finite(salesBlock?.actual) && salesBlock.actual !== 0
    ? gpBlock.actual / salesBlock.actual
    : null
  const cashNow = payload.cash?.cash_estimated_as_of ?? payload.cash?.cash_at_close ?? cash?.[period]?.actual
  const floor = payload.cash?.min_cash_floor
  const rows: OperatingRow[] = []

  if (sales) {
    const off = finite(salesDelta?.deltaPct) && salesDelta.deltaPct < -0.05
    rows.push({
      label: 'Revenue pace', group: 'Outcome', current: finite(salesDelta?.deltaPct) ? formatSignedPct(salesDelta.deltaPct) : 'Not prepared',
      currentValue: salesDelta?.deltaPct, targetValue: 0, goal: 'on plan', owner: 'CEO', status: off ? 'Off' : 'On', tone: off ? 'bad' : 'good',
      trend: trendValues(sales.trend_12m),
    })
  }
  if (gp) {
    rows.push({
      label: 'Gross margin', group: 'Outcome', current: finite(grossMargin) ? formatShare(grossMargin) : 'Not prepared',
      currentValue: grossMargin, goal: 'not set', owner: 'Ops', status: 'No goal', tone: 'quiet',
      trend: trendValues(gp.trend_12m),
    })
  }
  if (finite(cashNow)) {
    const off = finite(floor) && cashNow < floor
    rows.push({
      label: 'Cash buffer', group: 'Outcome', current: formatCurrencyExact(cashNow), currentValue: cashNow,
      targetValue: floor, goal: finite(floor) ? `${formatCurrencyExact(floor)} floor` : 'not set', owner: 'CEO',
      status: finite(floor) ? (off ? 'Off' : 'On') : 'No goal', tone: finite(floor) ? (off ? 'bad' : 'good') : 'quiet',
      trend: trendValues(cash?.trend_12m).length ? trendValues(cash?.trend_12m) : trendValues(payload.cash?.series_13w),
      trendLabel: 'Cash trend',
    })
  }
  for (const labor of payload.labor?.labor_pct_of_sales_by_location || []) {
    const value = labor.ttm ?? labor.ytd ?? labor[period]
    const off = finite(value) && finite(labor.target) && value > labor.target + 0.02
    rows.push({
      label: `${labor.label} labor %`, group: 'Controllable input', current: finite(value) ? formatShare(value) : 'Not prepared',
      currentValue: value, targetValue: labor.target, goal: finite(labor.target) ? formatShare(labor.target) : 'not set', owner: 'Operations',
      status: finite(labor.target) ? (off ? 'Off' : 'On') : 'No goal', tone: finite(labor.target) ? (off ? 'bad' : 'good') : 'quiet',
      trend: [labor.mtd, labor.qtd, labor.ytd, labor.ttm].filter(finite),
      trendLabel: `${labor.label} MTD, QTD, YTD, and trailing-year profile`,
    })
  }
  if (payload.inventory) {
    const count = payload.inventory.stockout_count
    rows.push({
      label: 'Products running low', group: 'Controllable input', current: String(count), currentValue: count,
      targetValue: 0, goal: '0', owner: 'Supply', status: count > 0 ? 'Off' : 'On', tone: count > 0 ? 'bad' : 'good',
      trend: (payload.inventory.stockout_risk || []).map((sku) => sku.days_of_supply).filter(finite),
      trendLabel: 'Days-of-supply profile for products running low',
    })
  }
  return rows
}

function scorecardReading(rows: OperatingRow[]): string {
  const withGoal = rows.filter((row) => row.status !== 'No goal')
  const off = withGoal.filter((row) => row.status === 'Off')
  const ungoaled = rows.filter((row) => row.status === 'No goal')
  const first = withGoal.length
    ? off.length
      ? `${off.length} of ${withGoal.length} measures with a goal are off it: ${off.map((row) => row.label).join(', ')}.`
      : `All ${withGoal.length} measures with a goal are meeting it.`
    : 'No measure has a goal set yet.'
  return `${first}${ungoaled.length ? ` ${ungoaled.length} more ${ungoaled.length === 1 ? 'measure has' : 'measures have'} no goal set, so they are reported without a verdict.` : ''}`
}

function decisionArea(decision: CeoDecision): CeoExploreSection {
  const haystack = [decision.rule, decision.headline, decision.body, ...(decision.tags || []), ...(decision.metric_refs || [])].join(' ').toLowerCase()
  if (/stock|inventory|sku|supply/.test(haystack)) return 'inventory'
  if (/customer|collection|receivable|dso/.test(haystack)) return 'customers'
  if (/labor|employee|headcount|utilization/.test(haystack)) return 'people'
  if (/location|channel|class|sales rep/.test(haystack)) return 'where'
  if (/cash|runway/.test(haystack)) return 'cash'
  if (/profit|margin|cost/.test(haystack)) return 'profit'
  return 'sales'
}

const MORNING_AREAS: Array<{ id: CeoExploreSection; label: string }> = [
  { id: 'scorecard', label: 'Scorecard' },
  { id: 'sales', label: 'Sales' },
  { id: 'profit', label: 'Profit' },
  { id: 'cash', label: 'Cash' },
  { id: 'where', label: 'Where it comes from' },
  { id: 'customers', label: 'Customers' },
  { id: 'people', label: 'People' },
  { id: 'inventory', label: 'Inventory' },
]

type ViewSpec = { id: string; title: string; reading: string }

function defaultView(section: CeoExploreSection): string {
  return ({ sales: 'pace', profit: 'months', cash: 'position', customers: 'top', where: 'location', people: 'labor', inventory: 'risk', subscriptions: 'bridge', morning: 'board', scorecard: 'board' } as Record<CeoExploreSection, string>)[section]
}

function alternateViews(section: CeoExploreSection, payload: CeoCockpitResponse): ViewSpec[] {
  const result: Partial<Record<CeoExploreSection, ViewSpec[]>> = {
    sales: [
      { id: 'pace', title: sectionTitle('sales', payload), reading: 'Cumulative sales against the selected reference and last year.' },
      { id: 'months', title: 'Sales by month', reading: 'Closed months plus the current partial month.' },
      { id: 'needed', title: 'Needed per day to hit plan', reading: 'The daily run rate required for the rest of the period.' },
      { id: 'location', title: 'By location', reading: 'The operating places contributing to this result.' },
    ],
    profit: [
      { id: 'months', title: 'Profit by month', reading: 'Closed months plus the current partial month.' },
      { id: 'margin', title: 'Margins by month', reading: 'Gross margin across the same monthly window.' },
      { id: 'pace', title: 'Profit pace this month', reading: 'Cumulative profit against the selected reference.' },
      { id: 'stairs', title: 'Sales → gross profit → profit', reading: 'What stayed after product costs and operating costs.' },
    ],
    cash: [
      { id: 'position', title: 'Where cash stands', reading: 'Cash now against the floor and the projected low point.' },
      { id: 'projection', title: 'Next 13 weeks', reading: 'The projected cash path; dashed because it is a forecast.' },
      { id: 'thennow', title: 'Then and now', reading: 'Cash at the last close, a year ago, and today.' },
      { id: 'ar', title: 'Owed to you', reading: 'Open receivables and collection speed.' },
      { id: 'cover', title: 'Cover and floor', reading: 'How much operating room the current balance provides.' },
    ],
    where: (payload.dimensions || []).filter((dimension) => !['not_applicable', 'hidden'].includes(dimension.availability?.status)).slice(0, 7).map((dimension) => ({
      id: dimension.kind, title: `By ${dimension.label.toLowerCase()}`, reading: `Sales contribution by ${dimension.label.toLowerCase()}.`,
    })),
    customers: [
      { id: 'top', title: 'Your biggest customers', reading: 'Sales by customer and cumulative concentration.' },
      { id: 'movement', title: 'Who bought more, who bought less', reading: 'The movement from the prior month into this one.' },
      { id: 'ar', title: 'Receivables at risk', reading: 'Customers with overdue balances.' },
      { id: 'concentration', title: 'How concentrated', reading: 'Top-customer shares of trailing sales.' },
      { id: 'new', title: 'New customers', reading: 'New logos across the current periods.' },
    ],
    people: [
      { id: 'labor', title: 'Labor as a share of sales', reading: 'Trailing ratios so pay periods and billing line up.' },
      { id: 'headcount', title: 'Headcount by team', reading: 'Current people by operating team.' },
      { id: 'rpe', title: 'Sales per person', reading: 'Trailing revenue divided by current headcount.' },
      { id: 'trend', title: 'Headcount, last 13 months', reading: 'The latest headcount for each team and its direction.' },
      { id: 'utilization', title: 'Utilization by person', reading: 'Billable utilization for businesses that track time.' },
    ],
    inventory: [
      { id: 'risk', title: 'Running low', reading: 'Days of supply against the replenishment threshold.' },
      { id: 'top', title: 'Best sellers, last 30 days', reading: 'Item sales with gross margin.' },
      { id: 'categories', title: 'On hand by category', reading: 'Inventory value by category.' },
      { id: 'overstock', title: 'Sitting too long', reading: 'Value tied up in products with excess supply.' },
      { id: 'summary', title: 'Stock health', reading: 'The operating inventory measures in one view.' },
    ],
  }
  return result[section] || [{ id: defaultView(section), title: sectionTitle(section, payload), reading: '' }]
}

function monthlyPoints(metric: CeoScorecardMetric | undefined) {
  const trend = (metric?.trend_12m || []).filter((point) => finite(point.value))
  return trend.map((point, index, all) => ({
    label: point.period?.slice(5, 7) || String(index + 1),
    value: point.value as number,
    complete: index < all.length - 1 || metric?.mtd?.complete !== false,
  }))
}

function dimensionForView(payload: CeoCockpitResponse, view: string) {
  return (payload.dimensions || []).find((dimension) => dimension.kind === view)
    || (payload.dimensions || []).find((dimension) => !['not_applicable', 'hidden'].includes(dimension.availability?.status))
}

function rowReference(row: CeoDimensionRow, period: CeoPeriodKind, basis: CeoBasisKind): number | null {
  const block = row[period]
  if (basis === 'plan') return block.plan?.expected_to_date ?? null
  if (basis === 'forecast') return block.forecast?.expected_to_date ?? null
  if (basis === 'prior_year') return block.prior_year?.value ?? null
  return block.prior_period?.value ?? null
}

function ComparisonBars({ rows, period, basis, caption }: { rows: CeoDimensionRow[]; period: CeoPeriodKind; basis: CeoBasisKind; caption: string }) {
  const clean = rows.slice(0, 10).map((row) => ({ row, actual: row[period]?.actual || 0, reference: rowReference(row, period, basis) }))
  const domain = Math.max(1, ...clean.flatMap(({ actual, reference }) => [actual, reference || 0]))
  return <figure className="ceo-comparison-bars" aria-label={caption}>
    {clean.map(({ row, actual, reference }) => {
      const delta = finite(reference) && reference !== 0 ? (actual - reference) / Math.abs(reference) : null
      return <div className="ceo-comparison-row" key={row.key}>
        <span className="ceo-comparison-label"><b>{row.label}</b><small>{formatShare(row.share_ytd)} of year</small></span>
        <span className="ceo-comparison-track">
          <i style={{ width: `${Math.max(1.5, (actual / domain) * 100)}%` }} />
          {finite(reference) ? <em style={{ left: `${Math.min(100, (reference / domain) * 100)}%` }} title={`${BASIS_LABEL[basis]} ${formatCurrencyExact(reference)}`} /> : null}
        </span>
        <span className="ceo-comparison-value"><b>{formatCurrencyExact(actual)}</b><small className={delta != null && delta < 0 ? 'is-bad' : 'is-good'}>{delta == null ? `no ${BASIS_LABEL[basis]}` : `${formatSignedPct(delta)} vs ${BASIS_LABEL[basis]}`}</small></span>
      </div>
    })}
    <figcaption>The bar is actual; the tick is {BASIS_LABEL[basis]} expected by now.</figcaption>
  </figure>
}

function SummaryTiles({ items }: { items: Array<{ label: string; value: string; note?: string; tone?: string }> }) {
  return <div className="ceo-explore-summary-tiles">{items.map((item) => <article key={item.label} className={item.tone ? `is-${item.tone}` : ''}><span className="ceo-kicker">{item.label}</span><b>{item.value}</b>{item.note ? <small>{item.note}</small> : null}</article>)}</div>
}

function inventoryCategoryRows(payload: CeoCockpitResponse): Array<{ label: string; value: number; sublabel: string }> {
  const inventory = payload.inventory
  if (!inventory) return []
  const fromPanel = (inventory.by_category || []).map((row) => ({ label: row.label, value: row.ytd?.actual || row.mtd?.actual || 0, sublabel: '' })).filter((row) => row.value > 0)
  if (fromPanel.length) return fromPanel
  const categories = new Map<string, { value: number; low: number; items: number }>()
  for (const sku of inventory.skus || []) {
    const label = sku.category || 'Uncategorized'
    const current = categories.get(label) || { value: 0, low: 0, items: 0 }
    current.value += sku.on_hand_value
    current.items += 1
    if (sku.stockout_risk) current.low += 1
    categories.set(label, current)
  }
  return [...categories.entries()].map(([label, item]) => ({ label, value: item.value, sublabel: `${item.low} of ${item.items} running low` })).sort((a, b) => b.value - a.value)
}

function ExploreVisual({ payload, period, basis, section, view, fallback }: { payload: CeoCockpitResponse; period: CeoPeriodKind; basis: CeoBasisKind; section: CeoExploreSection; view: string; fallback?: ReactNode }) {
  const rows = operatingRows(payload, period, basis)
  const sales = periodMetric(payload, 'sales') || periodMetric(payload, 'revenue')
  const gp = periodMetric(payload, 'gross_profit')
  const ni = periodMetric(payload, 'net_income')
  if (section === 'morning') {
    const decisions = (Array.isArray(payload.decisions) ? payload.decisions : []).filter((decision) => decision.kind !== 'celebrate')
    return <div className="ceo-explore-board">{MORNING_AREAS.filter((area) => area.id !== 'people' || payload.labor).filter((area) => area.id !== 'inventory' || payload.inventory).map((area) => {
      const matches = decisions.filter((decision) => decisionArea(decision) === area.id)
      const top = matches[0]
      return <article key={area.id} className={matches.length ? 'is-bad' : 'is-quiet'}><span className="ceo-kicker">{area.label}</span><b>{matches.length ? `${matches.length} to decide` : 'Quiet'}</b><small>{top?.headline || (area.id === 'cash' ? `Cash ${formatCurrencyExact(payload.cash?.cash_estimated_as_of ?? payload.cash?.cash_at_close ?? 0)} as of yesterday` : 'No exception raised')}</small></article>
    })}</div>
  }
  if (section === 'scorecard') {
    return <div className="ceo-operating-tiles">{rows.map((row) => <article key={row.label} className={`is-${row.tone}`}><span className="ceo-kicker">{row.label}</span><b>{row.current}</b><span className="ceo-operating-spark">{(row.trend || []).length >= 2 ? <Sparkline values={row.trend || []} width={112} height={34} ariaLabel={row.trendLabel || `${row.label} trend`} /> : <i aria-label="History building" />}</span><small>{row.status === 'No goal' ? '○ no goal set' : `${row.status === 'Off' ? '▲ off' : '● on'} · goal ${row.goal}`}</small></article>)}</div>
  }
  if (section === 'sales') {
    if (view === 'months') return <WeeksBarsChart weeks={monthlyPoints(sales)} caption="Sales by month" />
    if (view === 'needed') {
      const block = sales?.[period]
      const periodWindow = payload.calendar?.[period]
      const daysLeft = Math.max((periodWindow?.days_in_period || 0) - (periodWindow?.days_elapsed || 0), 0)
      const target = block?.plan?.full_period
      const needed = finite(target) && finite(block?.actual) && daysLeft > 0 ? Math.max(0, (target - block.actual) / daysLeft) : null
      const run = finite(block?.actual) && (periodWindow?.days_elapsed || 0) > 0 ? block.actual / (periodWindow?.days_elapsed || 1) : null
      return <TargetBulletsChart rows={[{ label: 'Needed / day', value: needed ?? 0, target: run }, { label: 'Run rate so far', value: run ?? 0 }, { label: 'Last year / day', value: finite(block?.prior_year?.value) ? block.prior_year.value / Math.max(periodWindow?.days_elapsed || 0, 1) : 0 }]} unit="usd" caption="Needed per day to hit plan" />
    }
    if (view === 'location') {
      const dim = dimensionForView(payload, 'location')
      return <RankedBarsChart rows={(dim?.rows || []).slice(0, 8).map((row) => ({ label: row.label, value: row[period]?.actual || 0, sublabel: `${Math.round(row.share_ytd * 100)}% of year` }))} unit="usd" caption="Sales by location" />
    }
    return <>{fallback}</>
  }
  if (section === 'profit') {
    if (view === 'pace') {
      const block = ni?.[period]
      const delta = block ? basisDelta(block, basis) : null
      return <SummaryTiles items={[
        { label: 'Profit so far', value: finite(block?.actual) ? formatCurrencyExact(block.actual) : 'Not prepared' },
        { label: 'Projected period', value: finite(block?.pace?.projection) ? formatCurrencyExact(block.pace.projection) : 'Not prepared' },
        { label: `vs ${BASIS_LABEL[basis]}`, value: finite(delta?.deltaPct) ? formatSignedPct(delta.deltaPct) : 'Not prepared', tone: finite(delta?.deltaPct) && delta.deltaPct < 0 ? 'bad' : 'good' },
      ]} />
    }
    if (view === 'margin') {
      const salesTrend = sales?.trend_12m || []
      const gpTrend = gp?.trend_12m || []
      const months = gpTrend.map((point, index) => {
        const salesValue = salesTrend[index]?.value
        const value = finite(point.value) && finite(salesValue) && salesValue !== 0 ? point.value / salesValue : 0
        return { period: point.period, label: point.period?.slice(5, 7) || String(index + 1), value }
      }).filter((point) => point.value > 0)
      const usual = months.length ? months.reduce((sum, item) => sum + item.value, 0) / months.length : 0
      return <MarginTrendChart months={months} usual={usual} caption="Gross margin by month" />
    }
    if (view === 'stairs') {
      return <TargetBulletsChart rows={[{ label: 'Sales', value: sales?.[period]?.actual || 0 }, { label: 'Gross profit', value: gp?.[period]?.actual || 0 }, { label: 'Profit', value: ni?.[period]?.actual || 0 }]} unit="usd" caption="Sales to gross profit to profit" />
    }
    return <WeeksBarsChart weeks={monthlyPoints(ni)} caption="Profit by month" />
  }
  if (section === 'cash') {
    const cash = payload.cash
    const now = cash?.cash_estimated_as_of ?? cash?.cash_at_close
    if (view === 'thennow') {
      const metric = periodMetric(payload, 'cash')
      return <TargetBulletsChart rows={[{ label: 'A year ago', value: metric?.[period]?.prior_year?.value || 0 }, { label: 'Last close', value: metric?.[period]?.prior_close?.value ?? cash?.cash_at_close ?? 0 }, { label: 'Now', value: now || 0 }]} unit="usd" caption="Cash then and now" />
    }
    if (view === 'ar') {
      return <SummaryTiles items={[{ label: 'Open receivables', value: formatCurrencyExact(cash?.ar_open ?? payload.customers?.ar_total ?? 0) }, { label: 'Days to get paid', value: finite(cash?.dso) ? `${Math.round(cash.dso)} days` : 'Not prepared' }, { label: 'Change', value: finite(cash?.dso_delta) ? `${cash.dso_delta > 0 ? '+' : ''}${Math.round(cash.dso_delta)} days` : 'Not prepared' }]} />
    }
    if (view === 'cover') {
      return <SummaryTiles items={[{ label: 'Months covered', value: finite(cash?.months_of_cash) ? `${cash.months_of_cash.toFixed(1)} mo` : finite(cash?.runway_months) ? `${cash.runway_months.toFixed(1)} mo` : 'Not prepared' }, { label: 'Floor', value: formatCurrencyExact(cash?.min_cash_floor ?? 0) }, { label: '13-week low', value: finite(cash?.min_projected_cash_13w) ? formatCurrencyExact(cash.min_projected_cash_13w) : 'Not prepared' }]} />
    }
    const low = finite(cash?.min_projected_cash_13w) && finite(now) && Math.abs(now - cash.min_projected_cash_13w) < Math.abs(now) * 0.15
      ? null
      : cash?.min_projected_cash_13w
    return <CashPositionChart now={now} floor={cash?.min_cash_floor} low13w={low} weeksAhead={trendValues(cash?.series_13w)} caption={view === 'projection' ? 'Projected cash over the next 13 weeks' : 'Cash now against the floor and projected low'} />
  }
  if (section === 'where') {
    const dim = dimensionForView(payload, view)
    return <ComparisonBars rows={dim?.rows || []} period={period} basis={basis} caption={`Sales by ${dim?.label || 'operating lens'}`} />
  }
  if (section === 'customers') {
    const customers = payload.customers
    if (view === 'concentration') return <SummaryTiles items={[{ label: 'Top customer', value: formatShare(customers.concentration.top1_share) }, { label: 'Top 5', value: formatShare(customers.concentration.top5_share) }, { label: 'Top 10', value: formatShare(customers.concentration.top10_share) }, { label: 'Named accounts', value: String(customers.top.length) }]} />
    if (view === 'new') return <SummaryTiles items={[{ label: 'This month', value: String(customers.new_logos.mtd) }, { label: 'This quarter', value: String(customers.new_logos.qtd) }, { label: 'This year', value: String(customers.new_logos.ytd), tone: 'good' }]} />
    if (view === 'ar') return <RankedBarsChart rows={(customers.at_risk || []).slice(0, 8).map((row) => ({ label: row.customer, value: row.overdue_ar, sublabel: `${formatCurrencyExact(row.over_60_ar)} over 60 days` }))} unit="usd" caption="Receivables at risk" />
    if (view === 'movement') {
      const movement = customers.movement
      return <SummaryTiles items={movement ? [
        { label: 'Starting sales', value: formatCurrencyExact(movement.start) },
        { label: 'New', value: formatCurrencyExact(movement.new), tone: 'good' },
        { label: 'Lost / contraction', value: formatCurrencyExact(Math.abs(movement.churn + movement.contraction)), tone: movement.churn + movement.contraction < 0 ? 'bad' : 'quiet' },
        { label: 'Ending sales', value: formatCurrencyExact(movement.end) },
      ] : [{ label: 'Movement bridge', value: 'Not prepared', note: 'Two comparable closed periods are needed.' }]} />
    }
    return <RankedBarsChart rows={(customers.top || []).slice(0, 10).map((row) => ({ label: row.label, value: row[period]?.actual || 0, sublabel: `${Math.round(row.share_ytd * 100)}% of year` }))} unit="usd" caption="Biggest customers" />
  }
  if (section === 'people') {
    const labor = payload.labor
    if (!labor) return null
    if (view === 'rpe') return <SummaryTiles items={[{ label: 'Sales per person', value: finite(labor.revenue_per_employee) ? formatCurrencyExact(labor.revenue_per_employee) : 'Not prepared' }, { label: 'People', value: String(labor.headcount) }, { label: 'Utilization', value: finite(labor.utilization?.overall) ? formatShare(labor.utilization.overall) : 'Not prepared' }]} />
    if (view === 'utilization') {
      const utilizationRows = (labor.utilization?.by_employee || []).filter((row) => finite(row.utilization))
      return utilizationRows.length
        ? <RankedBarsChart rows={utilizationRows.map((row) => ({ label: row.label, value: row.utilization || 0 }))} unit="pct" caption="Utilization by person" />
        : <SummaryTiles items={[{ label: 'Utilization', value: 'Not tracked', note: 'Connect billable time to prepare this view.' }]} />
    }
    if (view === 'headcount' || view === 'trend') return <RankedBarsChart rows={(labor.headcount_by_department || []).map((row) => ({ label: row.label, value: row.count, sublabel: `${trendValues(row.series_13m)[0] ?? row.count} a year ago` }))} unit="count" caption="Headcount by team" />
    return <TargetBulletsChart rows={(labor.labor_pct_of_sales_by_location || []).map((row) => ({ label: row.label, value: row.ttm ?? row.ytd ?? row[period] ?? 0, target: row.target }))} unit="pct" caption="Labor as a share of sales" />
  }
  if (section === 'inventory') {
    const inventory = payload.inventory
    if (!inventory) return null
    if (view === 'top') return <RankedBarsChart rows={(inventory.top_by_revenue || []).slice(0, 10).map((row) => ({ label: row.item_name, value: row.revenue_30d, sublabel: `${row.category || 'Uncategorized'}${finite(row.revenue_30d) && row.revenue_30d !== 0 ? ` · ${Math.round((row.margin_30d / row.revenue_30d) * 100)}% margin` : ''}` }))} unit="usd" caption="Best sellers in the last 30 days" />
    if (view === 'categories') return <RankedBarsChart rows={inventoryCategoryRows(payload).slice(0, 10)} unit="usd" caption="Inventory by category" />
    if (view === 'overstock') return <CoverBarsChart rows={(inventory.overstock || []).slice(0, 8).map((row) => ({ label: row.item_name, category: row.category, days_of_supply: row.days_of_supply, out_of_stock: false, detail: `${formatCurrencyExact(row.on_hand_value)} on hand` }))} thresholdDays={90} mode="overstock" caption="Products sitting too long" />
    if (view === 'summary') return <SummaryTiles items={[{ label: 'On hand', value: formatCurrencyExact(inventory.on_hand_value), note: `${Math.round(inventory.on_hand_units).toLocaleString()} units` }, { label: 'Days of supply', value: finite(inventory.days_of_supply) ? `${Math.round(inventory.days_of_supply)}` : 'Not prepared' }, { label: 'Turns / year', value: finite(inventory.turns) ? `${inventory.turns.toFixed(1)}×` : 'Not prepared' }, { label: 'Running low', value: String(inventory.stockout_count), tone: inventory.stockout_count ? 'bad' : 'good' }]} />
    return <CoverBarsChart rows={(inventory.stockout_risk || []).slice(0, 12).map((row) => ({ label: row.item_name, category: row.category, days_of_supply: row.days_of_supply, out_of_stock: row.qty_on_hand <= 0, detail: `${Math.round(row.qty_on_hand)} on hand · ${Math.round(row.units_30d)} sold / 30d` }))} thresholdDays={14} mode="stockout" caption="Products running low" />
  }
  return <>{fallback}</>
}

function ExploreDetailTable({ payload, period, basis, section, view }: { payload: CeoCockpitResponse; period: CeoPeriodKind; basis: CeoBasisKind; section: CeoExploreSection; view: string }) {
  const opRows = operatingRows(payload, period, basis)
  let title = ''
  let subtitle = ''
  let headers: string[] = []
  let cells: ReactNode[][] = []
  if (section === 'scorecard') {
    title = 'Operating scorecard'; subtitle = `through ${payload.as_of_date} · outcomes first, then the levers that move them`
    headers = ['Measure', 'Current', 'Trend / profile', 'Goal', 'Owner', 'Status']
    cells = opRows.map((row) => [`${row.label}|${row.group}`, row.current, (row.trend || []).length > 1 ? <Sparkline values={row.trend || []} width={82} height={24} ariaLabel={row.trendLabel || `${row.label} trend`} /> : <span className="ceo-history-building">history building</span>, row.goal, row.owner, row.status])
  } else if (section === 'customers') {
    title = 'Customers'; subtitle = 'sales, concentration, and receivables risk'
    headers = ['Customer', period.toUpperCase(), 'Share of year', 'Rank']
    cells = (payload.customers?.top || []).map((row) => [row.label, formatCurrencyExact(row[period]?.actual ?? 0), formatShare(row.share_ytd), `#${row.rank}`])
  } else if (section === 'inventory' && payload.inventory) {
    title = 'Products'; subtitle = 'top by last-30-day sales · company-wide on hand'
    headers = ['Product', 'On hand', 'Value', 'Sold / 30d', 'Sales / 30d', 'Margin', 'Days of supply']
    cells = (payload.inventory.skus || []).slice().sort((a, b) => b.revenue_30d - a.revenue_30d).slice(0, 25).map((row) => [`${row.item_name}|${row.category || 'Uncategorized'}${row.sku ? ` · ${row.sku}` : ''}`, Math.round(row.qty_on_hand).toLocaleString(), formatCurrencyExact(row.on_hand_value), Math.round(row.units_30d).toLocaleString(), formatCurrencyExact(row.revenue_30d), row.revenue_30d ? formatShare(row.margin_30d / row.revenue_30d) : '—', finite(row.days_of_supply) ? `${Math.round(row.days_of_supply)} days` : 'Out'])
  } else if (section === 'where') {
    const dim = dimensionForView(payload, view)
    title = `Sales by ${dim?.label.toLowerCase() || 'operating lens'}`; subtitle = 'the detail behind the picture'
    headers = [dim?.label || 'Row', period.toUpperCase(), `vs ${BASIS_LABEL[basis]}`, 'vs last year', 'Share of year', 'Margin', 'Rank']
    cells = (dim?.rows || []).map((row) => { const actual = row[period]?.actual || 0; const reference = rowReference(row, period, basis); return [row.label, formatCurrencyExact(actual), finite(reference) && reference !== 0 ? formatSignedPct((actual - reference) / Math.abs(reference)) : '—', finite(row[period]?.prior_year?.delta_pct) ? formatSignedPct(row[period].prior_year.delta_pct) : '—', formatShare(row.share_ytd), finite(row.gross_margin_pct_ytd) ? formatShare(row.gross_margin_pct_ytd) : '—', `#${row.rank}`] })
  } else return null
  const open = ['scorecard', 'customers', 'inventory'].includes(section)
  return <details className="ceo-explore-table-card" open={open}><summary><span><b>{title}</b><small>{subtitle}</small></span><span>{open ? 'Hide' : `Show all ${cells.length} rows`} ▾</span></summary><div className="ceo-explore-table-scroll"><table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{cells.map((row, rowIndex) => <tr key={`${String(row[0])}-${rowIndex}`}>{row.map((cell, index) => { const split = index === 0 && typeof cell === 'string' ? cell.split('|') : null; const primary = split ? split[0] : cell; const secondary = split?.[1] || ''; return <td key={`${rowIndex}-${index}`}><b>{primary}</b>{secondary ? <small>{secondary}</small> : null}</td> })}</tr>)}</tbody></table></div></details>
}

export function CeoExploreFrame({
  payload,
  period,
  basis,
  section,
  onSectionChange,
  onLensChange,
  onExplainDecision,
  panels,
}: CeoExploreFrameProps) {
  const [heroViews, setHeroViews] = useState<Partial<Record<CeoExploreSection, string>>>({})
  const ribbon = selectScorecardView(payload, period)
  const collections = collectionsRibbonItem(payload)
  const destinations: ExploreDestination[] = [
    { id: 'morning', label: 'This morning' },
    { id: 'scorecard', label: 'Scorecard' },
    { id: 'where', label: 'Where it comes from' },
    ...(payload.labor ? [{ id: 'people' as const, label: 'People' }] : []),
    ...(payload.inventory ? [{ id: 'inventory' as const, label: 'Inventory' }] : []),
    ...(payload.saas ? [{ id: 'subscriptions' as const, label: 'Subscriptions' }] : []),
  ]
  const metric = metricForSection(payload, section)
  const metricBlock = metric?.[period]
  const referencePosition = metric && metric.kind !== 'point_in_time'
    ? basisDelta(metricBlock, basis)
    : null
  const referenceDelta = metric && referencePosition
    ? inlineDelta(referencePosition.deltaAbs, referencePosition.deltaPct, metric.polarity)
    : null
  const priorYearDelta = metric
    ? inlineDelta(metricBlock?.prior_year?.delta_abs, metricBlock?.prior_year?.delta_pct, metric.polarity)
    : null
  const closeComparison = metricBlock?.prior_close || metricBlock?.prior_period
  const closeDelta = metric && metric.kind === 'point_in_time'
    ? inlineDelta(closeComparison?.delta_abs, closeComparison?.delta_pct, metric.polarity)
    : null
  const decisions = decisionsForSection(payload, section)
  const inactive = (Object.keys(panels) as CeoExploreSection[]).filter((id) => id !== section)
  const views = alternateViews(section, payload)
  const scorecardViews: Array<{ id: CeoExploreSection; title: string; view: string; reading: string }> = [
    { id: 'morning', title: 'This morning', view: 'board', reading: 'What needs attention now.' },
    { id: 'sales', title: 'Sales', view: 'months', reading: 'How the selected period is going.' },
    { id: 'profit', title: 'Profit', view: 'months', reading: 'What is staying after costs.' },
    { id: 'where', title: 'Where it comes from', view: defaultView('where'), reading: 'Where the result is coming from.' },
    ...(payload.labor ? [{ id: 'people' as const, title: 'People', view: 'labor', reading: 'Labor and capacity.' }] : []),
    ...(payload.inventory ? [{ id: 'inventory' as const, title: 'Inventory', view: 'risk', reading: 'Stock health and replenishment.' }] : []),
  ]
  const activeView = heroViews[section] || defaultView(section)
  const activeViewSpec = views.find((view) => view.id === activeView) || views[0]
  const dataReads = governedReads(payload, section, period, basis, activeViewSpec?.id || activeView)
  const reads: ExploreRead[] = [
    ...dataReads,
    ...decisions.map((decision) => ({ id: decision.id, headline: decision.headline, body: decision.body, severity: decision.severity, decision })),
  ].filter((read, index, all) => all.findIndex((candidate) => candidate.headline === read.headline) === index).slice(0, 6)
  const opRows = operatingRows(payload, period, basis)
  const heroTitle = activeViewSpec?.title || sectionTitle(section, payload)
  const selectView = (nextView: string) => {
    setHeroViews((current) => ({ ...current, [section]: nextView }))
    if (section === 'where') onLensChange?.(nextView as CeoDimensionKind)
  }
  const heroReading = section === 'scorecard'
    ? scorecardReading(opRows)
    : section === 'morning'
      ? `${payload.attention_count} ${payload.attention_count === 1 ? 'item needs' : 'items need'} attention — click any area to open it.`
      : section === 'customers'
        ? `Top 5 are ${formatShare(payload.customers?.concentration?.top5_share)} of sales; ${payload.customers?.top?.[0]?.label || 'the top customer'} alone ${formatShare(payload.customers?.concentration?.top1_share)}.`
        : section === 'people' && payload.labor
          ? `Trailing 12 months so pay periods and billing line up${payload.labor.labor_pct_of_sales_by_location.some((row) => finite(row.target)) ? '; targets shown where set.' : '; no targets set yet.'}`
          : section === 'inventory' && payload.inventory
            ? `${payload.inventory.stockout_count} ${payload.inventory.stockout_count === 1 ? 'product' : 'products'} under the replenishment threshold.`
            : section === 'where'
              ? `${payload.calendar?.[period]?.label || period.toUpperCase()}; the tick is the ${BASIS_LABEL[basis]} expected by now.`
              : activeViewSpec?.reading || metricReading(metric, period, basis, payload)

  return (
    <div className="ceo-explore" data-testid="ceo-explore">
      <div className="ceo-explore-ribbon" aria-label="Company scorecard">
        {ribbon.map(({ metric: item, block }) => {
          const destination = METRIC_DESTINATIONS[item.id] || 'scorecard'
          const trend = trendValues(item.trend_12m)
          const spark = item.mtd?.complete === false && trend.length > 1 ? trend.slice(0, -1) : trend
          return (
            <button
              type="button"
              key={item.id}
              className={`ceo-explore-rib is-${ribbonTone(item, period, basis)}${section === destination ? ' is-active' : ''}`}
              onClick={() => onSectionChange(destination)}
            >
              <span className="ceo-kicker">{item.label}</span>
              <span className="ceo-explore-rib-main">
                <b>{block.actual == null ? 'Not prepared' : formatCurrencyExact(block.actual)}</b>
                {spark.length >= 2 ? <Sparkline values={spark} width={54} height={20} ariaLabel={`${item.label} trend`} /> : null}
              </span>
              <small>{compactDelta(item, period, basis)}</small>
            </button>
          )
        })}
        {collections ? (
          <button
            type="button"
            className={`ceo-explore-rib is-${collections.kind === 'celebrate' ? 'pos' : collections.kind === 'watch' ? 'watch' : 'neg'}${section === 'customers' ? ' is-active' : ''}`}
            onClick={() => onSectionChange('customers')}
          >
            <span className="ceo-kicker">Collections</span>
            <span className="ceo-explore-rib-main">
              <b>{formatCurrencyCompact(collections.exposure_value || 0).replace(/\.0(?=[KMB])/, '')}</b>
            </span>
            <small>{collectionsDetail(collections)}</small>
          </button>
        ) : null}
      </div>

      <nav className="ceo-explore-sections" aria-label="Explore sections">
        {destinations.map((item) => {
          const count = item.id === 'morning' ? payload.attention_count : null
          return (
            <button
              type="button"
              key={item.id}
              className={section === item.id ? 'is-active' : ''}
              onClick={() => onSectionChange(item.id)}
            >
              {item.label}{count ? <span>{count}</span> : null}
            </button>
          )
        })}
      </nav>

      <section
        className="ceo-explore-hero"
        data-testid={`ceo-explore-section-${section}`}
        data-fg-tour={section === 'cash' ? 'ceo-cash' : section === 'customers' ? 'ceo-customers' : undefined}
      >
        <header className="ceo-explore-hero-head">
          <p className="ceo-kicker">{PANEL_LABELS[section]}</p>
          <h2>{heroTitle}</h2>
          <p>{heroReading}</p>
          {section === 'where' && views.length ? (
            <div className="ceo-explore-lenses" aria-label="Choose where sales come from">
              {views.map((view) => {
                const dimension = dimensionForView(payload, view.id)
                return <button type="button" key={view.id} className={view.id === activeViewSpec?.id ? 'is-active' : ''} onClick={() => selectView(view.id)}>
                  {dimension?.label || view.title.replace(/^By /, '')}
                  {dimension?.availability?.coverage_pct != null && dimension.availability.coverage_pct < 0.999 ? <small>{Math.round(dimension.availability.coverage_pct * 100)}%</small> : null}
                </button>
              })}
            </div>
          ) : null}
          {metric ? (
            <div className="ceo-explore-lead">
              <strong>{metric[period]?.actual == null ? 'Not prepared' : formatCurrencyExact(metric[period].actual)}</strong>
              <span>{metric.label.toLowerCase()} · {payload.calendar?.[period]?.label || period.toUpperCase()}</span>
              <div className="ceo-explore-kpis">
                {metric.kind === 'point_in_time' && closeDelta ? (
                  <span>vs last close <b className={`is-${closeDelta.tone}`}>{closeDelta.text}</b></span>
                ) : null}
                {metric.kind !== 'point_in_time' && referenceDelta ? (
                  <span>vs {BASIS_LABEL[basis]} so far <b className={`is-${referenceDelta.tone}`}>{referenceDelta.text}</b></span>
                ) : null}
                {priorYearDelta ? (
                  <span>vs last year <b className={`is-${priorYearDelta.tone}`}>{formatSignedPct(metricBlock?.prior_year?.delta_pct)}</b></span>
                ) : null}
                {metricBlock?.pace?.projection != null && Number.isFinite(metricBlock.pace.projection) ? (
                  <span>projected {period === 'mtd' ? 'month' : 'period'} <b>{formatCurrencyExact(metricBlock.pace.projection)}</b></span>
                ) : null}
                {metricBlock?.pace?.status && !['unknown', 'insufficient'].includes(metricBlock.pace.status) ? (
                  <em className={`is-${metricBlock.pace.status}`}>{metricBlock.pace.status.replace('_', ' ')}</em>
                ) : null}
              </div>
            </div>
          ) : null}
        </header>
        <div className="ceo-explore-hero-body">
          <ExploreVisual payload={payload} period={period} basis={basis} section={section} view={activeViewSpec?.id || activeView} fallback={panels[section]} />
        </div>
      </section>

      <details className="ceo-explore-provenance">
        <summary><span aria-hidden="true">●</span> How these numbers are figured</summary>
        <div>
          <b>Read from</b><span>the governed ledger, sales documents, balances, and the current workbook plan</span>
          <b>Through</b><span>{payload.as_of_date}, the last day with posted activity</span>
          <b>Comparisons</b><span>prorated to the selected period and the company’s reporting calendar</span>
        </div>
      </details>

      {reads.length ? (
        <section className="ceo-explore-reads">
          <div className="ceo-explore-strip-head"><span className="ceo-kicker">What this says</span><small>click a line for its evidence</small></div>
          <div className="ceo-explore-read-list">
            {reads.map((read) => (
              <button type="button" key={read.id} onClick={() => read.decision && onExplainDecision?.(read.decision)} className={read.decision ? '' : 'is-governed-read'}>
                <i className={`is-${read.severity}`} aria-hidden="true" />
                <span><b>{read.headline}</b> {read.body}</span>
                <span aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {section === 'morning' ? (
        <section className="ceo-explore-more ceo-explore-decisions-strip">
          <div className="ceo-explore-strip-head"><span className="ceo-kicker">Today&apos;s list</span><small>click one for its evidence</small></div>
          <div className="ceo-explore-more-strip">
            {(Array.isArray(payload.decisions) ? payload.decisions : []).map((decision) => (
              <button type="button" key={decision.id} onClick={() => onExplainDecision?.(decision)} className={`is-${decision.severity}`}>
                <b>{decision.kind === 'celebrate' ? 'Good news' : decision.kind === 'watch' ? 'Watch' : 'Decide'}</b>
                <strong>{decision.headline}</strong>
                <span>{decision.body}</span>
              </button>
            ))}
          </div>
        </section>
      ) : section === 'scorecard' ? (
        <section className="ceo-explore-more">
          <div className="ceo-explore-strip-head"><span className="ceo-kicker">More views</span><small>click a card to make it the big picture</small></div>
          <div className="ceo-explore-more-strip">
            {scorecardViews.map((item) => (
              <button type="button" key={item.id} onClick={() => onSectionChange(item.id)}>
                <b>{item.title}</b>
                <div className="ceo-explore-mini-visual" aria-hidden="true"><ExploreVisual payload={payload} period={period} basis={basis} section={item.id} view={item.view} fallback={panels[item.id]} /></div>
                <span>{item.reading}</span>
              </button>
            ))}
          </div>
        </section>
      ) : views.length > 1 ? (
        <section className="ceo-explore-more">
          <div className="ceo-explore-strip-head"><span className="ceo-kicker">More views</span><small>scroll → · click a card to make it the big picture</small></div>
          <div className="ceo-explore-more-strip">
            {views.filter((view) => view.id !== activeViewSpec?.id).map((view) => (
              <button type="button" key={view.id} onClick={() => selectView(view.id)}>
                <b>{view.title}</b>
                <div className="ceo-explore-mini-visual" aria-hidden="true"><ExploreVisual payload={payload} period={period} basis={basis} section={section} view={view.id} /></div>
                <span>{view.reading}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <ExploreDetailTable payload={payload} period={period} basis={basis} section={section} view={activeViewSpec?.id || activeView} />

      {/* Keep inactive panels mounted so a section failure stays isolated and
          local state (expanded rows, selected lens) survives switching views. */}
      <div className="ceo-explore-hidden-panels" aria-hidden="true">
        {inactive.map((id) => <div key={id}>{panels[id]}</div>)}
      </div>
    </div>
  )
}
